export interface SelectedFile {
    lastModified: number
    name: string
    size: number
    type: string
    content: string
}

const _URL = window.URL || window.webkitURL
export function readFile(e: Event): Promise<File> {
    const file = (e.target as HTMLInputElement).files?.[0]
    if (!file) {
        return Promise.reject()
    }

    return Promise.resolve(file)
}

export function asDataURL(file: File): Promise<SelectedFile> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)

        reader.onload = (readerEvent) => {
            const content = readerEvent.target?.result
            if (content) {
                resolve({ ...file, content: content as string })
            } else {
                reject()
            }
        }
    })
}

export function asBlobURL(file: File): Promise<SelectedFile> {
    return Promise.resolve({ ...file, content: _URL.createObjectURL(file) })
}

export const toFile =
    (filename: string) =>
    (dataurl: string): Promise<File> => {
        const arr = dataurl.split(',')
        const mime = arr[0].match(/:(.*?);/)?.[1]

        return fetch(dataurl)
            .then((res) => res.arrayBuffer())
            .then((buf) => new File([buf], filename, { type: mime }))
    }

export function toBlob(dataurl: string): Promise<Blob> {
    const arr = dataurl.split(',')
    const mime = arr[0].match(/:(.*?);/)?.[1]

    return fetch(dataurl)
        .then((res) => res.arrayBuffer())
        .then((buf) => new Blob([buf], { type: mime }))
}

export async function revokeBlobURL(url: string): Promise<void> {
    _URL.revokeObjectURL(url)
}

export const getField = <T, K extends keyof T>(field: K) =>
    function (input: T): Promise<T[K]> {
        return Promise.resolve(input[field])
    }

export function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'Anonymous'
        img.addEventListener('load', async function () {
            // Don't ask why... This prevents the "empty image" bug on iOS.
            window.setTimeout(() => {
                resolve(img)
            }, 1)
        })
        img.src = src
    })
}

export async function toCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
    const image = await loadImage(dataUrl)
    const canvas = document.createElement('canvas')
    canvas.width = image.width
    canvas.height = image.height

    const context = canvas.getContext('2d')
    context?.drawImage(image, 0, 0)

    return canvas
}

export const scale = (factor: (img: HTMLImageElement) => number) =>
    function (img: HTMLImageElement): Promise<HTMLCanvasElement> {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        const scaleRatio = factor(img)
        canvas.width = img.width * scaleRatio
        canvas.height = img.height * scaleRatio
        ctx?.drawImage(img, 0, 0, img.width * scaleRatio, img.height * scaleRatio)

        return Promise.resolve(canvas)
    }

export const loadCanvas = scale(() => 1)

export const toDataURL = (type: string) =>
    function (canvas: HTMLCanvasElement): Promise<string> {
        return Promise.resolve(canvas.toDataURL(type))
    }

export const chain = <T, U>(m1: (args: T) => Promise<U>, m2: (args1: T) => (args2: U) => void) =>
    async function (args: T): Promise<U> {
        const result = await m1(args)
        await m2(args)(result)

        return result
    }

export const swap =
    <T, U, V>(m: (args: T) => (args: U) => Promise<V>) =>
    (args1: U) =>
    (args2: T): Promise<V> =>
        m(args2)(args1)

export const pass =
    <T, U>(m: (args: U) => void) =>
    (result: T) =>
    async (args: U): Promise<T> => {
        await m(args)
        return result
    }

export const skip =
    <T>(m: (a: T) => void) =>
    async (a: T): Promise<T> => {
        await m(a)
        return a
    }

export const log = skip(console.log)
